import { sendStaffPatientMessageEmail } from '@/lib/staffPatientMessageEmail';

export const EMPTY_MESSAGE_ATTACHMENT_CONTENT = '(Fil vedhæftet)';

export interface SentPatientMessageRef {
  id: string;
  clinic_id: string;
}

function asSentMessage(value: unknown): SentPatientMessageRef {
  if (!value || typeof value !== 'object') {
    throw new Error('send_patient_message returned no row');
  }
  const row = value as { id?: unknown; clinic_id?: unknown };
  if (typeof row.id !== 'string' || typeof row.clinic_id !== 'string') {
    throw new Error('send_patient_message returned an incomplete row');
  }
  return { id: row.id, clinic_id: row.clinic_id };
}

/**
 * Shared clinic → patient portal message send (body + optional attachments).
 * After the row and attachments are stored, notifies the patient by email.
 * The notifier receives only the message id, never the body.
 */
export async function sendPatientMessageCompose(input: {
  patientId: string;
  content: string;
  files: File[];
  sendMessage: (params: { patient_id: string; content: string }) => Promise<unknown>;
  uploadDocument: (params: {
    clinicId: string;
    patientId: string;
    file: File;
    tags: string[];
  }) => Promise<{ id: string }>;
  attachDocument: (params: { message_id: string; document_id: string }) => Promise<unknown>;
  /** Defaults to the Resend notification. Tests pass a stub. */
  notifyPatient?: (messageId: string) => void;
}): Promise<SentPatientMessageRef> {
  const trimmed = input.content.trim();
  if (!trimmed && input.files.length === 0) {
    throw new Error('empty_message');
  }

  const sent = asSentMessage(
    await input.sendMessage({
      patient_id: input.patientId,
      content: trimmed || EMPTY_MESSAGE_ATTACHMENT_CONTENT,
    }),
  );

  for (const file of input.files) {
    const doc = await input.uploadDocument({
      clinicId: sent.clinic_id,
      patientId: input.patientId,
      file,
      tags: ['message'],
    });
    await input.attachDocument({
      message_id: sent.id,
      document_id: doc.id,
    });
  }

  const notify = input.notifyPatient ?? sendStaffPatientMessageEmail;
  notify(sent.id);
  return sent;
}
